import {gql} from '@apollo/client';
import {DocumentNode} from 'graphql';
import React from 'react';

import {
  IngredientMeasurementUnit,
  MixtureMeasurementUnit,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import notifications from 'notifications';
import useDraftState from 'ui-library/hooks/useDraftState';
import useOnChangeOnly from 'ui-library/hooks/useOnChangeOnly';
import * as logging from 'util/Logging';

import * as MixtureActions from '../MixtureActions';
import {
  EditingIngredient,
  IngredientTableData,
  ingredientToUpdateIngredientParams,
  MaybeNewIngredient,
  QueryingIngredientTableProps,
} from '../MixtureConstants';
import {
  MixtureValidationError,
  validateIngredientAmount,
  validateIngredientComponentBioentity,
  validateIngredientUnits,
  validateMixtureAmount,
  validateMixtureAtMostOneQS,
  validateMixtureComponentBioentitiesAreUnique,
  validateMixtureUnits,
} from '../validation';
import {IngredientTableQuery, IngredientTableQueryVariables} from './__generated/IngredientTableQuery';
import {PresentationalIngredientTable} from './PresentationalIngredientTable';

type Ingredient = ExtractByTypename<IngredientTableQuery['mixture'], 'MixtureNode'>['ingredients'][number];

function validateNonEmptyIngredients(ingredients: Array<EditingIngredient>, mixtureId: string): boolean {
  try {
    ingredients.forEach((ingredient) => {
      validateIngredientAmount(ingredient.amount);
      validateIngredientComponentBioentity(ingredient.componentBioentity?.id, mixtureId);
      validateIngredientUnits(ingredient.units);
    });
    validateMixtureComponentBioentitiesAreUnique(
      ingredients
        .map(({componentBioentity}) => componentBioentity?.id)
        .filter((id): id is string => id != null)
    );
    validateMixtureAtMostOneQS(ingredients.map(({amount}) => amount));
  } catch (e) {
    if (e instanceof MixtureValidationError) {
      notifications.error(e.message);
      return false;
    }
    throw e;
  }
  return true;
}

function filterToNonEmptyIngredients(ingredients: Array<EditingIngredient>): Array<EditingIngredient> {
  return ingredients.filter(
    ({componentBioentity, amount, catalogIdentifier, componentLotValues}) =>
      componentBioentity || amount || catalogIdentifier || componentLotValues.text
  );
}

async function validateAndPersistMixtureChanges(
  mixtureId: string,
  isRegistered: boolean,
  mixtureAmount: string | null,
  mixtureUnits: MixtureMeasurementUnit,
  ingredients: Array<EditingIngredient>
): Promise<boolean> {
  // Returns boolean indicating whether changes were persisted or not
  try {
    validateMixtureAmount(mixtureAmount);
    validateMixtureUnits(mixtureUnits);
  } catch (e) {
    if (e instanceof MixtureValidationError) {
      notifications.error(e.message);
      return false;
    }
    throw e;
  }
  const nonEmptyIngredients = filterToNonEmptyIngredients(ingredients);
  if (!validateNonEmptyIngredients(nonEmptyIngredients, mixtureId)) {
    return false;
  }

  const updatedMixture = await MixtureActions.updateWithDataMods(mixtureId, isRegistered, {
    amount: mixtureAmount!,
    units: mixtureUnits,
    ingredients: nonEmptyIngredients.map((ingredient) =>
      ingredientToUpdateIngredientParams(ingredient as MaybeNewIngredient)
    ),
  });
  return updatedMixture != null;
}

export const INGREDIENT_TABLE_QUERY: DocumentNode = gql`
  query IngredientTableQuery($mixtureId: StrictString!) @skipAudit @engComponent(name: REGISTRY) {
    mixture(id: $mixtureId) {
      ...MixtureSpecificAttributesGraphQL
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('MixtureSpecificAttributes').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const BIOENTITY_TAGS_QUERY: DocumentNode = gql`
  query BioentityTagsQuery($bioentityId: StrictString!) @skipAudit @engComponent(name: REGISTRY) {
    bioentity(id: $bioentityId) {
      ... on FolderItemInterface {
        id
        tagsJson
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export interface IngredientTableProps extends QueryingIngredientTableProps {
  initialMixtureUnits: MixtureMeasurementUnit;
  initialMixtureAmount: string;
  initialIngredients: Array<Ingredient>;
  refetchMixtureData: () => void;
}

const DEFAULT_INGREDIENT_UNITS = IngredientMeasurementUnit.mL;

export function IngredientTable(props: IngredientTableProps): JSX.Element {
  const draftState = useDraftState<IngredientTableData>({
    mixtureUnits: props.initialMixtureUnits,
    mixtureAmount: props.initialMixtureAmount,
    ingredients: props.initialIngredients,
  });
  const {data, updateDraftData, isEditing, startEditing, stopEditing} = draftState;
  const {mixtureUnits, mixtureAmount, ingredients} = data;

  const setDraftIngredients = (updatedIngredients: Array<EditingIngredient>): void => {
    updateDraftData({ingredients: updatedIngredients});
  };

  const handleAddIngredient = (): void => {
    startEditing();
    setDraftIngredients([
      ...ingredients,
      {
        __typename: 'IngredientNode',
        id: null,
        componentBioentity: null,
        catalogIdentifier: null,
        componentLotValues: {
          __typename: 'ComponentLotValuesNode',
          text: null,
          bioentity: null,
          container: null,
        },
        amount: null,
        units: DEFAULT_INGREDIENT_UNITS,
        isInherited: false,
        inheritedTargetAmount: null,
        notes: null,
        isCatalogIdentifierInherited: false,
      },
    ]);
  };

  const handleUpdateIngredient = (ingredientIndex: number, updates: Partial<EditingIngredient>): void => {
    setDraftIngredients([
      ...ingredients.slice(0, ingredientIndex),
      {...ingredients[ingredientIndex], ...updates},
      ...ingredients.slice(ingredientIndex + 1),
    ]);
  };

  const handleDeleteIngredient = async (ingredientIndex: number): Promise<void> => {
    const newIngredients = [
      ...ingredients.slice(0, ingredientIndex),
      ...ingredients.slice(ingredientIndex + 1),
    ];
    if (isEditing) {
      setDraftIngredients(newIngredients);
    } else {
      // TODO(BNCH-13123) add confirmation modal
      await validateAndPersistMixtureChanges(
        props.mixtureId,
        props.isRegistered,
        mixtureAmount,
        mixtureUnits,
        newIngredients
      );
    }
  };

  const handleCompleteEdit = async (): Promise<void> => {
    const success = await validateAndPersistMixtureChanges(
      props.mixtureId,
      props.isRegistered,
      mixtureAmount,
      mixtureUnits,
      ingredients
    );
    if (success) {
      stopEditing();
    }
  };

  const {refetchMixtureData, parentLinkFileId, tagSchema} = props;

  useOnChangeOnly(() => {
    refetchMixtureData();
  }, [parentLinkFileId, tagSchema.id]);

  return (
    <PresentationalIngredientTable
      {...props}
      isLoading={false}
      draftState={draftState}
      onAddIngredient={handleAddIngredient}
      onUpdateIngredient={handleUpdateIngredient}
      onDeleteIngredient={handleDeleteIngredient}
      onCompleteEdit={handleCompleteEdit}
    />
  );
}

export default function QueryingIngredientTable(props: QueryingIngredientTableProps): JSX.Element {
  return (
    <BenchlingQuery<IngredientTableQuery, IngredientTableQueryVariables>
      query={INGREDIENT_TABLE_QUERY}
      variables={{mixtureId: props.mixtureId}}
      renderLoading={() => <PresentationalIngredientTable {...props} isLoading={true} />}
      renderCachedDataWhenRefetching={true}
    >
      {(data, {refetch}) => {
        if (isInaccessible(data.mixture)) {
          logging.warn(`Expected mixture to be accessible: ${props.mixtureId}`);
          return null;
        } else {
          const mixture = data.mixture;
          return (
            <IngredientTable
              {...props}
              initialMixtureAmount={mixture.amount}
              initialMixtureUnits={mixture.units}
              initialIngredients={mixture.ingredients}
              refetchMixtureData={refetch}
            />
          );
        }
      }}
    </BenchlingQuery>
  );
}
